import { useEffect, useRef, useCallback, useReducer, useMemo } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken, positionUnwinderAxiosApi } from 'src/Config/axiosApi';
import {
  EnrichedRuleStatus,
  OrdersResponse,
  TopOfBook,
  UnwindingPageState
} from '../types/unwinding.types';
import {
  LatencySample,
  recordReceived,
  useLatencyTracker
} from './useLatencyTracker';
import { createLiveStore, LiveStore } from '../live/createLiveStore';
import { useFlushOnVisible } from '../live/useFlushOnVisible';
export type { EnrichedRuleStatus } from '../types/unwinding.types';
export type UnwindLiveStore = LiveStore<EnrichedRuleStatus>;

export interface UnwindExecutionEvent {
  ruleId: string;
  pairName: string;
  makerPlatform: string;
  makerPrice: number;
  counterPrice: number;
  contracts: number;
  tnaPercent: number;
  volumeUsd: number;
}

export interface UnwindErrorEvent {
  ruleId: string;
  pairName: string;
  state: string;
  errorMessage: string;
}

// High-frequency `unwinding_state*` frames no longer flow through this
// reducer — they mutate the vanilla `unwindLiveStore` (see below) so only
// the rows whose slice changed re-render. The reducer keeps the low-rate
// execution / error / orders events, which the whole page reads.
type Action =
  | { type: 'EXECUTION'; payload: UnwindExecutionEvent }
  | { type: 'ERROR'; payload: UnwindErrorEvent }
  | { type: 'ORDERS'; payload: OrdersResponse };

interface State {
  /** Most recent execution event (for toasts/notifications) */
  lastExecution: UnwindExecutionEvent | null;
  /** Most recent error event */
  lastError: UnwindErrorEvent | null;
  orders: OrdersResponse | null;
}

const initialState: State = {
  lastExecution: null,
  lastError: null,
  orders: null
};

/**
 * Strip the server's `_meta` envelope before passing into the reducer.
 * Server wraps non-array payloads as `{ ...payload, _meta }` and array
 * payloads as `{ data: payload, _meta }`. Reducers don't need to know.
 */
/**
 * Phase 6 — project `UnwindingPageState` back into the legacy
 * `EnrichedRuleStatus` shape so existing row components keep working
 * during the bridging release. Position-side dictates which (bid, ask)
 * pair is displayed prominently. When `rule === null`, rule-scoped
 * fields use the same `state: 'no_rule'` defaults the legacy backend
 * produced for position-only rows.
 */
function projectUnwindingToLegacy(p: UnwindingPageState): EnrichedRuleStatus {
  const m = p.market;
  const r = p.rule;
  const pos = p.position;
  const polySide = pos.polyPosition?.side ?? 'yes';
  const kalshiSide = pos.kalshiPosition?.side ?? 'yes';
  return {
    ruleId: r?.ruleId ?? p.pairId,
    pairId: p.pairId,
    state: r?.state ?? 'no_rule',
    makerPlatform: r?.makerPlatform ?? 'kalshi',
    makerOrderId: r?.makerOrderId ?? null,
    makerPrice: r?.makerPrice ?? null,
    makerFilledQty: r?.makerFilledQty ?? 0,
    makerFilledPrice: r?.makerFilledPrice ?? 0,
    counterOrderId: r?.counterOrderId ?? null,
    counterFilledQty: r?.counterFilledQty ?? 0,
    lastStateChange: r?.lastStateChange ?? new Date().toISOString(),
    errorMessage: r?.errorMessage ?? null,
    pairName: m.pairName,
    expiryDate: m.expiryDate,
    daysToExpiry: m.daysToExpiry,
    polyBid: polySide === 'no' ? m.polyNoBid : m.polyYesBid,
    polyAsk: polySide === 'no' ? m.polyNoAsk : m.polyYesAsk,
    kalshiBid: kalshiSide === 'no' ? m.kalshiNoBid : m.kalshiYesBid,
    kalshiAsk: kalshiSide === 'no' ? m.kalshiNoAsk : m.kalshiYesAsk,
    simulation: r?.simulation ?? {
      polyAsMaker: null,
      kalshiAsMaker: null
    },
    kalshiConfig: r?.kalshiConfig ?? null,
    polymarketConfig: r?.polymarketConfig ?? null,
    maxVolumeContracts: r?.maxVolumeContracts ?? 0,
    maxVolumeHit: r?.maxVolumeHit ?? false,
    volumePeriod: r?.volumePeriod ?? 'day',
    volumeUsed: r?.volumeUsed ?? 0,
    volumeRemaining: r?.volumeRemaining ?? 0,
    kalshiMarketUrl: m.kalshiMarketUrl,
    polymarketMarketUrl: m.polymarketMarketUrl,
    simTna: r?.simTna,
    simSize: r?.simSize,
    simMaxVolume: r?.simMaxVolume,
    appliedSimTna: r?.appliedSimTna,
    appliedSimSize: r?.appliedSimSize,
    appliedSimMaxVolume: r?.appliedSimMaxVolume,
    hasPendingSim: r?.hasPendingSim,
    // Per-pair kill switch is owned by PositionGroup (backend
    // projection), independent of whether a rule exists. Treat missing
    // as ON so an old payload doesn't render the switch as OFF.
    unwinderEnabled: pos.unwinderEnabled !== false,
    // The rule's REAL armed state — the AUTHORITATIVE source for the per-row
    // on/off switch. Forwarded VERBATIM (not coerced to false): `undefined`
    // must survive so the switch's back-compat fallback to `unwinderEnabled`
    // can kick in on envelopes from an older backend that doesn't emit it yet.
    ruleArmed: p.ruleArmed,
    // SELL-direction sim from the Rust unwinder engine, ported through
    // the Phase-6 envelope. Forward whatever the backend sent — `null`
    // when the Rust cache has no entry. NEVER fall back to FE-local
    // computation here; the row component renders `—` instead.
    engineSim: p.engineSim ?? null,
    simCells: p.simCells ?? null,
    // LIVE engine believed position — forward whatever the backend sent
    // (`null` until a live envelope lands). Preserved against legacy
    // `unwind_status` clobbering in `mergePreservedFields` below.
    believedPosition: p.believedPosition ?? null
  };
}

function unwrap<T>(message: unknown): T {
  if (Array.isArray(message)) return message as T;
  if (
    message &&
    typeof message === 'object' &&
    '_meta' in (message as object)
  ) {
    const obj = message as Record<string, unknown> & { _meta?: unknown };
    if ('data' in obj) return obj.data as T;
    const rest = { ...obj };
    delete rest._meta;
    return rest as T;
  }
  return message as T;
}

/**
 * `unwinderEnabled` is a pair-scoped kill switch carried ONLY by the
 * Phase-6 `unwinding_state` / `unwinding_state_update` stream (which
 * `projectUnwindingToLegacy` always sets to a definite boolean). The
 * legacy `unwind_status` / `unwind_status_update` stream — still the
 * unwinder's COMPREHENSIVE update path, so we can't stop listening to it —
 * and the `/states` REST seed both OMIT the field. Every stream writes
 * this same Map, so a legacy event arriving with `unwinderEnabled:
 * undefined` would wipe the value the preceding `unwinding_state*` event
 * set; with both streams firing on every broadcast cycle the per-pair
 * switch then visibly FLIPS (false → undefined→renders ON → false → …).
 * Preserve the last known value whenever an incoming entry doesn't carry
 * the field — only the `unwinding_state*` stream may ever change it.
 *
 * `engineSim` + `simCells` have the same dual-stream problem — they
 * arrive on `unwinding_state*` envelopes only. A legacy `unwind_status`
 * event arriving with them undefined would wipe the value. Preserve
 * the last known cells the same way.
 */
function mergePreservedFields(
  prev: EnrichedRuleStatus | undefined,
  incoming: EnrichedRuleStatus
): EnrichedRuleStatus {
  if (!prev) return incoming;
  // The store dual-keys the same object under both `ruleId` and `pairId`,
  // so `prev` (the value at the canonical key) already carries whatever the
  // legacy ruleId/pairId double-lookup used to find.
  const pick = <K extends keyof EnrichedRuleStatus>(key: K) => prev[key];
  let out = incoming;
  if (incoming.unwinderEnabled === undefined) {
    const v = pick('unwinderEnabled');
    if (v !== undefined) out = { ...out, unwinderEnabled: v };
  }
  // `ruleArmed` rides `unwinding_state*` only — the legacy `unwind_status`
  // stream and the `/states` REST seed omit it. Preserve the last known value
  // so a legacy event arriving with it undefined never wipes the authoritative
  // armed bit the switch reads (same dual-stream hazard as `unwinderEnabled`).
  if (incoming.ruleArmed === undefined) {
    const v = pick('ruleArmed');
    if (v !== undefined) out = { ...out, ruleArmed: v };
  }
  if (incoming.engineSim === undefined) {
    const v = pick('engineSim');
    if (v !== undefined) out = { ...out, engineSim: v };
  }
  if (incoming.simCells === undefined) {
    const v = pick('simCells');
    if (v !== undefined) out = { ...out, simCells: v };
  }
  // Believed position rides `unwinding_state*` only — a legacy `unwind_status`
  // event arriving with it undefined would wipe the live value; preserve it.
  if (incoming.believedPosition === undefined) {
    const v = pick('believedPosition');
    if (v !== undefined) out = { ...out, believedPosition: v };
  }
  return out;
}

/** Reference / value compare for a top-of-book quote (price + size). */
function topEqual(a: TopOfBook | null, b: TopOfBook | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.price === b.price && a.size === b.size;
}

/**
 * Structural equality over the fields a row actually renders. Returns `true`
 * when `next` is render-equivalent to `prev` so the store can skip the update
 * (no per-key notify, no re-render). Nested objects that the row reads by
 * identity (`simulation`, `engineSim`, `simCells`, `believedPosition`,
 * configs) are compared by reference — the backend re-allocates them on every
 * real change, and a missing-field stream is handled by `mergePreservedFields`
 * BEFORE this runs, so a preserved field keeps its prior reference and compares
 * equal here.
 */
function rowEquals(prev: EnrichedRuleStatus, next: EnrichedRuleStatus): boolean {
  return (
    prev.ruleId === next.ruleId &&
    prev.pairId === next.pairId &&
    prev.state === next.state &&
    prev.pairName === next.pairName &&
    prev.expiryDate === next.expiryDate &&
    prev.daysToExpiry === next.daysToExpiry &&
    topEqual(prev.polyBid, next.polyBid) &&
    topEqual(prev.polyAsk, next.polyAsk) &&
    topEqual(prev.kalshiBid, next.kalshiBid) &&
    topEqual(prev.kalshiAsk, next.kalshiAsk) &&
    prev.simulation === next.simulation &&
    prev.engineSim === next.engineSim &&
    prev.simCells === next.simCells &&
    prev.believedPosition === next.believedPosition &&
    prev.unwinderEnabled === next.unwinderEnabled &&
    prev.ruleArmed === next.ruleArmed &&
    prev.maxVolumeContracts === next.maxVolumeContracts &&
    prev.maxVolumeHit === next.maxVolumeHit &&
    prev.volumeUsed === next.volumeUsed &&
    prev.volumeRemaining === next.volumeRemaining &&
    prev.simTna === next.simTna &&
    prev.simSize === next.simSize &&
    prev.simMaxVolume === next.simMaxVolume &&
    prev.appliedSimTna === next.appliedSimTna &&
    prev.appliedSimSize === next.appliedSimSize &&
    prev.appliedSimMaxVolume === next.appliedSimMaxVolume &&
    prev.hasPendingSim === next.hasPendingSim &&
    prev.kalshiConfig === next.kalshiConfig &&
    prev.polymarketConfig === next.polymarketConfig &&
    prev.kalshiMarketUrl === next.kalshiMarketUrl &&
    prev.polymarketMarketUrl === next.polymarketMarketUrl
  );
}

/**
 * Build the per-hook `unwindLiveStore`. Dual-keys each item under both
 * `ruleId` and `pairId` (deduped, falsy dropped) — mirroring the legacy
 * `pairId ?? ruleId` lookup. `merge` ports the Phase-6 field-preservation so a
 * legacy stream omitting `unwinderEnabled`/`engineSim`/`simCells`/
 * `believedPosition` never clobbers the value an `unwinding_state*` frame set.
 */
export function makeUnwindLiveStore(): UnwindLiveStore {
  return createLiveStore<EnrichedRuleStatus>({
    keysOf: s => {
      const keys: string[] = [];
      if (s.ruleId) keys.push(s.ruleId);
      if (s.pairId && s.pairId !== s.ruleId) keys.push(s.pairId);
      return keys;
    },
    equals: rowEquals,
    merge: mergePreservedFields
  });
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'EXECUTION':
      return { ...state, lastExecution: action.payload };
    case 'ERROR':
      return { ...state, lastError: action.payload };
    case 'ORDERS':
      return { ...state, orders: action.payload };
    default:
      return state;
  }
}

export function useUnwindingSocket() {
  const wsUrl = import.meta.env.VITE_POSITION_UNWINDER_URL;
  const accessToken = _getToken();

  const { socket } = useSocket((wsUrl || '') + '/unwind', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    // Pass auth as a callback so socket.io-client reads the current
    // token on every (re)connection. This ensures logout/refresh
    // updates propagate and we never reconnect with a stale token.
    auth: (cb: (data: { token: string }) => void) => cb({ token: _getToken() })
  });

  const [state, dispatch] = useReducer(reducer, initialState);
  const hasSubscribed = useRef(false);

  // Vanilla external store for the high-frequency `unwinding_state*` stream.
  // Created once per hook instance; rows read their own slice via
  // `useLiveState(store, id)` so a WS frame re-renders only the changed rows
  // (no page/tab re-render, no full Map copy). `merge` ports the Phase-6
  // field-preservation; `equals` skips render-equivalent frames.
  const unwindLiveStore = useMemo(() => makeUnwindLiveStore(), []);

  // Mirror of the opener: repaint on becoming visible, since the store holds
  // its notifications while the tab is hidden.
  useFlushOnVisible(unwindLiveStore);

  // REST seed: fetch the same EnrichedRuleState[] payload the WS broadcasts
  // and inject it into the reducer. Runs on mount and whenever the WS is
  // disconnected — so the table renders market data even when:
  //   - the user just opened the page and the WS handshake is in flight,
  //   - the WS auth fails or the connection is briefly dropped,
  //   - the Predictions subsystem is OFF (the old REST fallback path on
  //     `polyPos.topBid`/`kalshiPos.topBid` from prediction-arbitrage-manager
  //     is undefined in that case — position-unwinder owns the prices now).
  // The live WS updates always override the REST seed via the store's merge.
  useEffect(() => {
    if (!wsUrl || !accessToken) return;
    if (socket.connected) return; // WS will deliver the snapshot via subscribe_unwind

    let cancelled = false;
    positionUnwinderAxiosApi
      .get<EnrichedRuleStatus[]>('/states')
      .then(({ data }) => {
        if (cancelled) return;
        if (!Array.isArray(data)) return;
        unwindLiveStore.applyUpdate(data);
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('[UnwindSocket] REST seed failed:', err?.message ?? err);
      });

    return () => {
      cancelled = true;
    };
  }, [wsUrl, accessToken, socket.connected, unwindLiveStore]);

  // Latency tracker: each WS event handler below records the receive
  // timestamp and the first-rAF-after-receipt timestamp. Samples are
  // batched and flushed every 5 s via `latency_report` to the server,
  // which converts them into the wsDeliveryMs / reactRenderMs histograms
  // visible at /unwind/metrics/latency.
  const latencyBuf = useRef<LatencySample[]>([]);
  useLatencyTracker(socket, latencyBuf);

  // Subscribe on every connect/reconnect. `socket` object identity is stable,
  // so depending on it alone can miss the connected transition and skip the
  // initial `subscribe_unwind` forever.
  useEffect(() => {
    const subscribeIfNeeded = () => {
      if (!hasSubscribed.current) {
        socket.emit('subscribe_unwind');
        hasSubscribed.current = true;
      }
    };
    const markDisconnected = () => {
      hasSubscribed.current = false;
    };

    socket.on('connect', subscribeIfNeeded);
    socket.on('disconnect', markDisconnected);

    if (socket.connected) subscribeIfNeeded();
    else markDisconnected();

    return () => {
      socket.off('connect', subscribeIfNeeded);
      socket.off('disconnect', markDisconnected);
    };
  }, [socket, socket.connected]);

  // Phase 7 (2026-05-23): the legacy `unwind_status` / `unwind_status_update`
  // listeners + their effect handlers were deleted. The Phase-6
  // `unwinding_state*` stream is now comprehensive on the backend
  // (every legacy emit site has a paired Phase-6 emit) so consuming
  // both into the same Map produced 2× dispatches, 2× Map rebuilds,
  // and forced the now-deleted `withPreservedUnwinderEnabled` clobber
  // workaround. The legacy server emits will be removed in a follow-up
  // backend commit after this FE change ships.
  const { lastMessage: executionMessage } =
    useSocketEvent<UnwindExecutionEvent>(socket, 'unwind_execution');

  const { lastMessage: errorMessage } = useSocketEvent<UnwindErrorEvent>(
    socket,
    'unwind_error'
  );

  useEffect(() => {
    if (executionMessage) {
      recordReceived(latencyBuf.current, 'unwind_execution', executionMessage);
      dispatch({
        type: 'EXECUTION',
        payload: unwrap<UnwindExecutionEvent>(executionMessage)
      });
    }
  }, [executionMessage]);

  useEffect(() => {
    if (errorMessage) {
      recordReceived(latencyBuf.current, 'unwind_error', errorMessage);
      dispatch({
        type: 'ERROR',
        payload: unwrap<UnwindErrorEvent>(errorMessage)
      });
    }
  }, [errorMessage]);

  // ── Phase 6 — split-shape listeners. Backend emits these alongside
  //    the legacy `unwind_status` / `unwind_status_update` for one
  //    bridging release. We project envelopes back to the legacy shape
  //    so the existing reducer + row components keep working. Phase 7
  //    removes the legacy listeners + projection.
  const { lastMessage: unwindingStateMessage } = useSocketEvent<
    UnwindingPageState[]
  >(socket, 'unwinding_state');
  const { lastMessage: unwindingStateUpdateMessage } = useSocketEvent<
    UnwindingPageState[]
  >(socket, 'unwinding_state_update');

  useEffect(() => {
    if (unwindingStateMessage) {
      recordReceived(
        latencyBuf.current,
        'unwinding_state',
        unwindingStateMessage
      );
      const payload = unwrap<UnwindingPageState[]>(unwindingStateMessage);
      if (!Array.isArray(payload)) return;
      // Full snapshot → store full-replace (evicts pairs no longer present).
      unwindLiveStore.applySnapshot(payload.map(projectUnwindingToLegacy));
    }
  }, [unwindingStateMessage, unwindLiveStore]);

  useEffect(() => {
    if (unwindingStateUpdateMessage) {
      recordReceived(
        latencyBuf.current,
        'unwinding_state_update',
        unwindingStateUpdateMessage
      );
      const payload = unwrap<UnwindingPageState[]>(unwindingStateUpdateMessage);
      if (!Array.isArray(payload)) return;
      // Incremental update → store merge (changed keys only).
      unwindLiveStore.applyUpdate(payload.map(projectUnwindingToLegacy));
    }
  }, [unwindingStateUpdateMessage, unwindLiveStore]);

  const { lastMessage: ordersMessage } = useSocketEvent<OrdersResponse>(
    socket,
    'unwind_orders'
  );

  useEffect(() => {
    if (ordersMessage) {
      recordReceived(latencyBuf.current, 'unwind_orders', ordersMessage);
      dispatch({
        type: 'ORDERS',
        payload: unwrap<OrdersResponse>(ordersMessage)
      });
    }
  }, [ordersMessage]);

  // Stable `getState` reading from the live store. Used by the off-row
  // consumers that need a one-shot snapshot (the "Engine position" drawer's
  // `believedPosition`) without subscribing to per-key updates. Rows
  // themselves read their slice reactively via `useLiveState(store, id)`.
  const getState = useCallback(
    (ruleId: string): EnrichedRuleStatus | undefined =>
      unwindLiveStore.getKeySnapshot(ruleId),
    [unwindLiveStore]
  );

  /** Manually inject enriched states (e.g., from REST response as WS fallback) */
  const injectStates = useCallback(
    (states: EnrichedRuleStatus[]) => {
      if (!Array.isArray(states)) {
        console.warn(
          '[UnwindSocket] injectStates called with non-array, ignoring:',
          states
        );
        return;
      }
      unwindLiveStore.applyUpdate(states);
    },
    [unwindLiveStore]
  );

  return {
    isConnected: socket.connected,
    /** The vanilla external store — rows subscribe per-key via `useLiveState`. */
    store: unwindLiveStore,
    getState,
    injectStates,
    lastExecution: state.lastExecution,
    lastError: state.lastError,
    orders: state.orders
  };
}
